// Calls Missing Outcome card + table (Tab, Nov 3 2026 — Joey: "Yes build this"). Sits next to Booking Rate in Sales Metrics and
// follows the Total / On Webby / Post Webby toggle. Data: GET /api/webinar2/missing-outcome/:id (services/missing-outcome.ts).
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type View = "total" | "on-webby" | "post-webby";
type Row = {
  personKey: string; lead: string; segment: "on-webby" | "post-webby"; bucket: "eoc" | "cancelled" | "missing" | "upcoming";
  callTime: string | null; callTimeEt: string | null; closer: string | null; setter: string | null; hoursSinceCall: number | null;
  daysOverdue: number | null; stage: "new" | "24h" | "48h" | null; reason: string; reasonKind: string; evidence: string[]; tags: string[];
  bookings: number; closeLeadUrl: string | null; eocDraftBy?: string | null;
};
type Counts = { booked: number; missing: number; cancelledNoEoc: number; eoc: number; upcoming: number; bookingRows: number };
type Data = { webinarId: string; generatedAt: string; rule: string; counts: Record<View, Counts>; rows: Row[]; sources: Record<string, string> };

const cache = new Map<string, Data>();
const inflight = new Map<string, Promise<Data | null>>();
function load(webinarId: string, traffic: string, attempt = 0): Promise<Data | null> {
  const key = `${webinarId}|${traffic}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key)!);
  if (inflight.has(key)) return inflight.get(key)!;
  const p = fetch(`/api/webinar2/missing-outcome/${webinarId}?traffic=${traffic}`)
    .then((r) => r.json())
    .then((j) => { inflight.delete(key); if (j?.success) { cache.set(key, j.data); return j.data as Data; } return null; })
    .catch(() => { inflight.delete(key); return null; })
    .then((d) => (d || attempt >= 2 ? d : new Promise<Data | null>((res) => setTimeout(() => res(load(webinarId, traffic, attempt + 1)), 4000))));
  inflight.set(key, p);
  return p;
}

const VIEW_LABEL: Record<View, string> = { total: "Total", "on-webby": "On Webby", "post-webby": "Post Webby" };
const KIND_COLOR: Record<string, string> = {
  "eoc-draft": "bg-amber-500/15 text-amber-400", "setter-note": "bg-violet-500/15 text-violet-300", "cancel-email": "bg-sky-500/15 text-sky-300",
  iclosed: "bg-sky-500/15 text-sky-300", payment: "bg-emerald-500/15 text-emerald-400", recording: "bg-emerald-500/15 text-emerald-400",
  "no-show-recording": "bg-red-500/15 text-red-400", "no-recording": "bg-red-500/10 text-red-300",
};
const KIND_LABEL: Record<string, string> = {
  "eoc-draft": "EOC draft", "setter-note": "Setter note", "cancel-email": "Cancel email", iclosed: "iClosed", payment: "Paid",
  recording: "Showed", "no-show-recording": "No-show", "no-recording": "No recording",
};

export function MissingOutcomeCard({ webinarId, traffic, view, compact, onHide }: { webinarId: string; traffic: string; view: View; compact?: boolean; onHide?: () => void }) {
  const [data, setData] = useState<Data | null>(cache.get(`${webinarId}|${traffic}`) ?? null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true; setFailed(false);
    const hit = cache.get(`${webinarId}|${traffic}`); setData(hit ?? null);
    if (!hit) load(webinarId, traffic).then((d) => { if (!live) return; if (d) setData(d); else setFailed(true); });
    return () => { live = false; };
  }, [webinarId, traffic]);
  const c = data?.counts[view];
  const tip = "Missing Outcome = booked people whose call is more than 2 hours past with no End of Call Report (matched by email, phone or name), no iClosed cancel / reschedule and no no-show. One row per person. Missing + cancelled (EOC not filed) + EOC'd (+ upcoming) = unique booked people. Click for the list and why each one is missing.";
  return (
    <>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        onClick={c ? () => setOpen(true) : undefined} title={tip} data-missing-outcome-card={webinarId}
        className={`relative group rounded-lg px-3 py-3 border transition-colors bg-muted/20 border-border/40 ${c ? "cursor-pointer hover:border-[var(--brand-primary)]/40 hover:bg-muted/30" : ""}`}>
        {onHide && (
          <button onClick={(e) => { e.stopPropagation(); onHide(); }}
            className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 opacity-0 group-hover:opacity-100 transition-all text-[10px] leading-none"
            title="Hide metric">×</button>
        )}
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Missing Outcome{c ? " ↗" : ""}<span className="ml-1 normal-case opacity-70" aria-label={tip}>ⓘ</span></div>
        <div className={`${compact ? "text-base" : "text-xl"} font-bold tabular-nums ${c && c.missing > 0 ? "text-red-400" : "text-foreground"}`}>
          {c ? c.missing : failed ? "—" : <span className="inline-block w-8 h-5 rounded bg-muted/40 animate-pulse align-middle" />}
        </div>
        {c && !compact && (
          <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5 leading-snug">
            of {c.booked} booked · {c.cancelledNoEoc} cancelled, EOC not filed · {c.eoc} EOC'd{c.upcoming ? ` · ${c.upcoming} upcoming` : ""}
          </div>
        )}
        {failed && !compact && <div className="text-[10px] text-muted-foreground/60 mt-0.5">couldn't load</div>}
      </motion.div>
      <AnimatePresence>
        {open && data && <MissingOutcomeModal data={data} view={view} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </>
  );
}

function MissingOutcomeModal({ data, view: initialView, onClose }: { data: Data; view: View; onClose: () => void }) {
  const [view, setView] = useState<View>(initialView);
  const [showCancelled, setShowCancelled] = useState(false);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const inView = (r: Row) => view === "total" || r.segment === view;
  const missing = data.rows.filter((r) => r.bucket === "missing" && inView(r));
  const cancelled = data.rows.filter((r) => r.bucket === "cancelled" && inView(r));
  const c = data.counts[view];
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} data-missing-outcome-table={data.webinarId}
        className="fixed left-1/2 top-[5vh] -translate-x-1/2 w-[min(1180px,96vw)] max-h-[90vh] bg-card border border-border rounded-xl z-[70] flex flex-col overflow-hidden"
        style={{ boxShadow: "0 12px 48px rgba(0,0,0,0.35)" }}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border shrink-0">
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-foreground">Calls Missing Outcome · {data.webinarId}</h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              <span className="text-red-400 font-semibold">{c.missing} missing</span> + {c.cancelledNoEoc} cancelled, EOC not filed + {c.eoc} EOC'd{c.upcoming ? ` + ${c.upcoming} upcoming` : ""} = {c.booked} unique booked people
              {c.bookingRows !== c.booked ? ` (${c.bookingRows} bookings on the Webinar Bookings card)` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["total", "on-webby", "post-webby"] as const).map((v) => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${view === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>{VIEW_LABEL[v]}</button>
              ))}
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors" title="Close">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="overflow-auto px-5 py-3">
          {missing.length === 0 ? (
            <div className="text-xs text-muted-foreground py-6 text-center">Every booked call in this view has an outcome.</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="text-left font-medium py-2 pr-3">Lead</th>
                  <th className="text-left font-medium py-2 pr-3 whitespace-nowrap">Call time (ET)</th>
                  <th className="text-left font-medium py-2 pr-3">Closer</th>
                  <th className="text-left font-medium py-2 pr-3">Setter</th>
                  <th className="text-right font-medium py-2 pr-3 whitespace-nowrap">Days overdue</th>
                  <th className="text-left font-medium py-2">Auto-reason</th>
                </tr>
              </thead>
              <tbody>
                {missing.map((r) => (
                  <tr key={r.personKey} className="border-b border-border/40 align-top" data-missing-row>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <div className="font-medium text-foreground">{r.closeLeadUrl ? <a href={r.closeLeadUrl} target="_blank" rel="noreferrer" className="hover:underline" title="Open in Close to file the EOC">{r.lead}</a> : r.lead}</div>
                      <div className="text-[10px] text-muted-foreground">{r.segment === "on-webby" ? "On Webby" : "Post Webby"}</div>
                      {r.tags.length > 0 && <span className="inline-block mt-1 mr-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-orange-500/15 text-orange-300" title={r.tags.join(" · ")}>duplicate</span>}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{r.callTimeEt ?? "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.closer ?? "—"}{r.eocDraftBy && r.eocDraftBy !== r.closer ? <div className="text-[10px] text-muted-foreground">EOC draft: {r.eocDraftBy}</div> : null}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{r.setter ?? "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <span className={r.stage === "48h" ? "text-red-400 font-semibold" : r.stage === "24h" ? "text-amber-400 font-semibold" : ""}>{r.daysOverdue ?? 0}</span>
                    </td>
                    <td className="py-2">
                      <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${KIND_COLOR[r.reasonKind] ?? "bg-muted/40 text-muted-foreground"}`}>{KIND_LABEL[r.reasonKind] ?? r.reasonKind}</span>
                      <span className="text-foreground/90">{r.reason}</span>
                      {r.evidence.length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{r.evidence.join(" · ")}</div>}
                      {r.tags.length > 0 && <div className="text-[10px] text-orange-300/80 mt-0.5">{r.tags.join(" · ")}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {cancelled.length > 0 && (
            <div className="mt-4">
              <button onClick={() => setShowCancelled((s) => !s)} className="text-[11px] text-muted-foreground hover:text-foreground">
                {showCancelled ? "▾" : "▸"} {cancelled.length} cancelled, EOC not filed (not counted as missing)
              </button>
              {showCancelled && (
                <table className="w-full text-xs mt-2">
                  <tbody>
                    {cancelled.map((r) => (
                      <tr key={r.personKey} className="border-b border-border/30">
                        <td className="py-1.5 pr-3 whitespace-nowrap font-medium">{r.lead}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{r.callTimeEt ?? "—"}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{r.closer ?? "—"}</td>
                        <td className="py-1.5 text-muted-foreground">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground/70 mt-4">{data.rule} Reasons checked in order: iClosed cancel / reschedule, a draft EOC in Close, setter notes in Close, a cancel email from the lead, a payment, a Callix recording. Lead name opens Close, where the closer files the 04. EOC Report. Updated {new Date(data.generatedAt).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET.</p>
        </div>
      </motion.div>
    </>
  );
}
